import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { DepthLabel } from "@/components/Shell";
import { countryName } from "@/lib/countries";

export default function RegionPlan({ projectId, project, onDiff }) {
  const [regions, setRegions] = useState([]);
  const [plan, setPlan] = useState(project?.plan || { regions: [], connectors: [], blueprints: [], features: [], ai_use: false });

  useEffect(() => { api.get("/regions/options").then(r => setRegions(r.data.regions)).catch(() => {}); }, []);

  const savePlan = async (next) => {
    setPlan(next);
    try {
      const { data } = await api.put(`/projects/${projectId}/plan`, next);
      onDiff?.(data.obligations_diff);
      const uk = next.regions.some(r => ["UK", "EU", "UK-EU"].includes(r));
      toast.success(uk ? "Plan updated — UK/EU connector view unlocked" : "Plan updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleRegion = (id) => {
    const has = plan.regions.includes(id);
    savePlan({ ...plan, regions: has ? plan.regions.filter(r => r !== id) : [...plan.regions, id] });
  };
  const baselineWarn = plan.regions.some(r => regions.find(x => x.id === r)?.depth === "BASELINE");

  return (
    <div className="k-container" data-testid="region-selector" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Globe size={18} color="var(--accent)" />
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Target regions</h3>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {regions.map(r => (
          <button key={r.id} data-testid={`region-${r.id}`}
            className={`k-badge ${plan.regions.includes(r.id) ? "k-badge-accent" : ""}`}
            style={{ cursor: "pointer", display: "flex", gap: 6 }} onClick={() => toggleRegion(r.id)}>
            {countryName(r.name)} <DepthLabel depth={r.depth} />
          </button>
        ))}
      </div>
      <label className="k-muted" style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
        <input type="checkbox" data-testid="ai-use-toggle" checked={!!plan.ai_use}
          onChange={e => savePlan({ ...plan, ai_use: e.target.checked })} />
        This app uses AI features for end users
      </label>
      {baselineWarn && (
        <p className="k-tag-warn" style={{ fontSize: 12, marginTop: 10 }} data-testid="baseline-warning">
          BASELINE regions carry generic safeguards only — verify locally with a professional.
        </p>
      )}
    </div>
  );
}

export function ObligationsDiff({ diff }) {
  if (!diff) return null;
  const delta = diff.crx_delta;
  const deltaClass = delta > 0 ? "k-tag-red" : delta < 0 ? "k-tag-green" : "k-tag-info";
  return (
    <div className="k-container" data-testid="obligations-diff" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>What obligations changed?</h3>
        <span className="k-badge">{diff.label}</span>
      </div>
      <div className="k-muted" style={{ fontSize: 13, marginBottom: 10 }}>
        CRX {diff.crx_before} → {diff.crx_after} <span className={deltaClass} style={{ fontWeight: 700 }}>({delta >= 0 ? "+" : ""}{delta})</span>
      </div>
      {diff.added?.length > 0 && <div style={{ marginBottom: 8 }}>
        <div className="k-tag-green" style={{ fontSize: 13, fontWeight: 600 }}>Controls added</div>
        {diff.added.map((c, i) => (
          <div key={i} className="k-control" style={{ marginTop: 4 }} data-testid="obligation-added">
            <span className="k-heading" style={{ fontSize: 13 }}>{c.control_id}</span> <DepthLabel depth={c.depth} />
            <div className="k-muted" style={{ fontSize: 11 }}>{c.topic} · cites {c.rule_id} · {c.reviewer} · {String(c.last_reviewed).slice(0, 10)}</div>
          </div>
        ))}
      </div>}
      {diff.removed?.length > 0 && <div style={{ marginBottom: 8 }}>
        <div className="k-tag-red" style={{ fontSize: 13, fontWeight: 600 }}>Controls removed</div>
        {diff.removed.map((c, i) => (
          <div key={i} className="k-control" style={{ marginTop: 4 }} data-testid="obligation-removed">
            <span className="k-heading" style={{ fontSize: 13 }}>{c.control_id}</span>
            <div className="k-muted" style={{ fontSize: 11 }}>{c.topic} · cited {c.rule_id}</div>
          </div>
        ))}
      </div>}
      {(!diff.added?.length && !diff.removed?.length) &&
        <p className="k-muted" style={{ fontSize: 13 }}>No control changes from this plan update.</p>}
      {diff.new_perimeter_flags?.length > 0 &&
        <p className="k-tag-warn" style={{ fontSize: 12 }}>Perimeter flags: {diff.new_perimeter_flags.join("; ")}</p>}
    </div>
  );
}
